import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, MapPin, ShoppingBasket, UtensilsCrossed, Car, Cross, Droplets, Flame, Briefcase, Fuel, Zap, Bath, Landmark,
  Wrench, Popcorn, ParkingCircle, Coffee, Pill, Stethoscope, FlaskConical, Accessibility, Store, Shirt, Gem, Camera, Building2,
  Phone, ShieldAlert, GraduationCap, Home as HomeIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useLocation as useGeo } from "@/context/LocationContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const ACTIVITIES = [
  ["lift_stuck", "Stuck in a lift"], ["medical", "Need a doctor / hospital"], ["travel", "On a trip"],
  ["cinema", "Going to the movies"], ["shopping", "Shopping"], ["wedding", "Planning a wedding"],
  ["jobs", "Looking for work"], ["home", "Home & daily needs"],
];
const HIDE_KEY = "f2n_ctx_assist_hidden";
// Every tile runs the shared search (/search → /api/search/universal). Nothing is pre-filled or invented.
const ESSENTIALS = {
  home: [["Groceries", "grocery", ShoppingBasket], ["Food delivery", "restaurant", UtensilsCrossed], ["Cab ride", "taxi", Car],
         ["Medical store", "pharmacy", Cross], ["Water supply", "water supply", Droplets], ["Gas booking", "gas agency", Flame], ["Local jobs", "jobs", Briefcase]],
  travel: [["Petrol pump", "petrol pump", Fuel], ["Restaurants", "restaurant", UtensilsCrossed], ["EV charging", "ev charging", Zap],
           ["Washroom", "washroom", Bath], ["ATM", "atm", Landmark], ["Medical store", "pharmacy", Cross], ["Tyre repair", "tyre repair", Wrench]],
  cinema: [["Restrooms", "washroom", Bath], ["Food & snacks", "snacks", Popcorn], ["Café", "cafe", Coffee], ["ATM", "atm", Landmark],
           ["Parking", "parking", ParkingCircle], ["Cab home", "taxi", Car], ["Pharmacy", "pharmacy", Pill]],
  medical: [["Medical store", "pharmacy", Cross], ["Doctors", "doctor", Stethoscope], ["Lab & tests", "diagnostic lab", FlaskConical],
            ["Blood bank", "blood bank", Droplets], ["Food", "restaurant", UtensilsCrossed], ["ATM", "atm", Landmark],
            ["Cab ride", "taxi", Car], ["Wheelchair", "wheelchair rental", Accessibility], ["Hospitals", "hospital", HomeIcon],
            ["Ambulance", null, Phone, "/ambulance"]],
  shopping: [["Grocery", "grocery", ShoppingBasket], ["Clothing", "clothing store", Shirt], ["Electronics", "electronics shop", Store],
             ["Jewellers", "jewellers", Gem], ["ATM", "atm", Landmark], ["Parking", "parking", ParkingCircle]],
  wedding: [["Marriage halls", "marriage hall", Building2], ["Caterers", "caterer", UtensilsCrossed], ["Jewellers", "jewellers", Gem],
            ["Photography", "photographer", Camera], ["Makeup artists", "makeup artist", Store]],
  jobs: [["Jobs", "jobs", Briefcase], ["Coaching", "coaching", GraduationCap], ["Cyber café", "cyber cafe", Store]],
  lift_stuck: [["Lift SOS", null, ShieldAlert, "/sos/lift"], ["Emergency numbers", null, Phone, "/emergency"], ["Lift repair", "lift repair", Wrench]],
};

function EssentialsGrid({ activity, loc }) {
  const items = ESSENTIALS[activity] || [];
  if (!items.length) return null;
  const geo = loc?.source === "gps" && typeof loc.lat === "number" ? `&lat=${loc.lat}&lng=${loc.lng}` : "";
  return (
    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="ctx-essentials">
      {items.map(([label, q, Icon, to]) => (
        <Link key={label} to={to || `/search?q=${encodeURIComponent(q)}${geo}&from=aina`} data-testid={`ctx-tile-${label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="flex flex-col items-center gap-1 rounded-2xl border border-stone-100 bg-stone-50 hover:bg-white hover:border-stone-300 p-2 text-center transition-colors">
          <Icon size={22} className="text-[#E05E36]" aria-hidden />
          <span className="text-[11px] font-bold text-stone-700 leading-tight">{label}</span>
        </Link>
      ))}
    </div>
  );
}

function PendingItems() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!user) { setItems([]); return; }
    api.get("/aina/context/pending").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [user]);
  if (!items.length) return null;
  return (
    <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="ctx-pending">
      <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1.5">{t("aina.waiting_on_you", "Waiting on you")}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <Link key={`${it.kind}-${i}`} to={it.href} data-testid={`ctx-pending-${it.kind}-${i}`}
                className="text-xs font-bold bg-white border border-amber-200 hover:border-amber-400 rounded-full px-3 py-1.5 transition-colors">{it.code ? t(`aina.pending.${it.code}`, it.label, { ...(it.vars || {}), status: t(`amb.status_short.${it.vars?.status}`, (it.vars?.status || "").toLowerCase()) }) : it.label}</Link>
        ))}
      </div>
    </div>
  );
}

function ActionLink({ a }) {
  const tel = a.href.startsWith("tel:");
  const onClick = (e) => {
    if (a.requires_confirmation && !window.confirm(`${a.label}?`)) e.preventDefault();
  };
  const cls = `inline-flex items-center h-9 px-3 rounded-full text-xs font-bold transition-colors ${a.id === "call_emergency" ? "bg-red-600 text-white hover:bg-red-700" : "bg-stone-900 text-white hover:bg-stone-700"}`;
  return tel || a.href.startsWith("http")
    ? <a href={a.href} onClick={onClick} className={cls} data-testid={`ctx-action-${a.id}`}>{a.label}</a>
    : <Link to={a.href} onClick={onClick} className={cls} data-testid={`ctx-action-${a.id}`}>{a.label}</Link>;
}

export function ContextAssist() {
  const { loc } = useGeo();
  const { country, t } = useI18n();
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === "1");
  const [activity, setActivity] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activity) return;
    const granted = loc?.source === "gps" && typeof loc.lat === "number";
    const params = { activity, country, loc_state: granted ? "confirmed" : "unknown", ...(granted && { lat: loc.lat, lng: loc.lng }) };
    let alive = true;
    setData(null);
    api.get("/aina/context/suggest", { params })
      .then((r) => alive && setData({ ...r.data, activity }))
      .catch(() => alive && setData({ error: true, actions: [], results: [], activity }));
    return () => { alive = false; };
  }, [activity, loc, country]);
  const shown = data && data.activity === activity ? data : null;

  if (hidden) {
    return <button onClick={() => { localStorage.removeItem(HIDE_KEY); setHidden(false); }} data-testid="ctx-show-btn"
                   className="mt-4 text-xs font-bold text-stone-500 underline">Show AINA suggestions</button>;
  }
  return (
    <section className="mt-5 rounded-3xl border border-stone-200 bg-white p-4 text-stone-800" data-testid="ctx-assist">
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-black uppercase tracking-widest text-stone-500">{t("ctx.title", "AINA · What are you doing right now?")}</div>
        <button onClick={() => { localStorage.setItem(HIDE_KEY, "1"); setHidden(true); }} aria-label="Hide suggestions"
                data-testid="ctx-hide-btn" className="ms-auto text-stone-400 hover:text-stone-700"><X size={16} /></button>
      </div>
      <PendingItems />
      <div className="mt-2 flex flex-wrap gap-2" data-f2n-fab-avoid>
        {ACTIVITIES.map(([id, label]) => (
          <button key={id} onClick={() => setActivity(activity === id ? null : id)} data-testid={`ctx-activity-${id}`}
                  className={`h-8 px-3 rounded-full text-xs font-bold border transition-colors ${activity === id ? "bg-[#E05E36] text-white border-[#E05E36]" : "border-stone-200 text-stone-700 hover:border-stone-400"}`}>
            {t(`ctx.${id}`, label)}
          </button>
        ))}
      </div>
      {activity && !shown && <div className="mt-3 text-xs text-stone-500" data-testid="ctx-loading">Finding real options…</div>}
      {shown && (
        <div className="mt-3 space-y-3" data-testid="ctx-result">
          <div className="flex flex-wrap gap-2">{shown.actions.map((a) => <ActionLink key={a.id} a={a} />)}</div>
          <EssentialsGrid activity={activity} loc={loc} />
          {shown.error && <div className="text-xs text-amber-700" data-testid="ctx-error">Suggestions unavailable right now — the buttons above and search still work.</div>}
          {shown.note && <div className="text-xs text-stone-500 flex items-center gap-1" data-testid="ctx-note"><MapPin size={12} /> {shown.note}</div>}
          {shown.results?.length > 0 ? (
            <ul className="divide-y divide-stone-100" data-testid="ctx-places">
              {shown.results.map((r, i) => (
                <li key={`${r.name}-${i}`} className="py-2 flex justify-between gap-2 text-sm">
                  <span className="font-bold text-stone-800">{r.name}<span className="text-stone-400 font-normal"> · {r.category}</span></span>
                  <span className="text-xs text-stone-500 shrink-0">{r.distance_km != null && `${r.distance_km.toFixed(1)} km · `}{r.provenance?.source}</span>
                </li>
              ))}
            </ul>
          ) : shown.anchor ? <div className="text-xs text-stone-500" data-testid="ctx-empty">No verified places found nearby from our map sources.</div> : null}
          <div className="text-[10px] text-stone-400">Based only on what you selected{shown.context?.location?.state === "confirmed" ? " and the location you shared" : ""}. AINA does not track you.</div>
        </div>
      )}
    </section>
  );
}
